"use client";

import type { UtilityBarOffice } from "~www/types/utility-bar";

import Chevron from "@homeinstead/icons/Chevron";
import Location from "@homeinstead/icons/Location";
import LocationTarget from "@homeinstead/icons/LocationTarget";
import { cn } from "@homeinstead/ui";
import { useState } from "react";
import { useOnClickOutside } from "usehooks-ts";
import { trackSearchToggleClick } from "~www/components/LocationSearchForm/events";
import { OfficeLocationSearch } from "~www/components/OfficeLocationSearch";
import { useCloseOnEsc } from "~www/hooks/useCloseOnEsc";

type LocationDetailsProps = {
  office?: UtilityBarOffice;
  officePageURL?: string;
};

function LocationDetails({ office, officePageURL }: LocationDetailsProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  function toggleOfficeSearch(isOpenOverride?: boolean) {
    const nextIsOpen = isOpenOverride ?? !isOpen;
    setIsOpen(nextIsOpen);
    trackSearchToggleClick(nextIsOpen);
  }

  // close the dialog on escape key press and click outside
  const containerRef = useCloseOnEsc<HTMLDivElement>(isOpen, () => {
    toggleOfficeSearch(false);
  });

  useOnClickOutside(containerRef, () => {
    if (!isOpen) {
      return;
    }

    toggleOfficeSearch(false);
  });

  function handleOfficeSearchToggle() {
    toggleOfficeSearch();
  }

  function handleOfficeSelect(canonicalPath: string) {
    toggleOfficeSearch(false);
    window.location.href = canonicalPath;
  }

  return (
    <div className="relative" ref={containerRef}>
      <button
        className="z-50 flex items-center"
        onClick={handleOfficeSearchToggle}
        type="button"
      >
        {office ? (
          <>
            <Location className="mr-2 xs:inline h-6 text-xs" />
            <span className="min-w-32 select-none font-bold">
              My Home Instead:{"\u00A0"}
            </span>

            {office.address && (
              <span className="truncate underline hover:no-underline">
                {office.address.city}, {office.address.stateProvince}
              </span>
            )}
          </>
        ) : (
          <>
            <LocationTarget className="mr-2 xs:inline fill-support-tints-400" />
            <span className="select-none font-bold">
              Find my nearest office location
            </span>
          </>
        )}
        <Chevron
          className={cn(
            `ml-2 xs:inline w-2 fill-support-tints-400 transition duration-200`,
            { "rotate-180": isOpen },
          )}
        />
      </button>

      <div
        aria-hidden={!isOpen}
        className={cn(
          "hidden xs:w-dvw starting:opacity-0 transition-all transition-discrete duration-200 md:w-auto",
          { "block opacity-100": isOpen },
          { "pointer-events-none opacity-0": !isOpen },
        )}
      >
        <OfficeLocationSearch
          className="fixed top-12 left-0 md:absolute md:top-[2.25rem]"
          office={office}
          officePageURL={officePageURL}
          onSelectOffice={handleOfficeSelect}
        />
      </div>
    </div>
  );
}

export default LocationDetails;
